/* LX — scripts des blocs maison. Sans dépendance ; s'appuie sur Dawn quand il est présent. */
(() => {
  /* ---------- Format monétaire (shop.money_format) ---------- */
  function formatMoney(cents, format) {
    const fmt = format || '€{{amount_with_comma_separator}}';
    const value = Math.round(Number(cents) || 0);
    const group = (n, decimals, thousands, decimal) => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, dec] = fixed.split('.');
      const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? withThousands + decimal + dec : withThousands;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return group(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  /* ---------- Offres groupées ---------- */
  const bundles = new Map();

  class LxBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const data = this.querySelector('[data-lx-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.moneyFormat = this.dataset.moneyFormat;
      this.formId = this.dataset.formId;
      this.mainVariantId = Number(this.dataset.variantId);
      this.tiers = Array.from(this.querySelectorAll('[data-tier]'));

      this.tiers.forEach((tier) => {
        tier.addEventListener('click', (event) => {
          if (event.target.closest('select')) return;
          this.select(tier);
        });
        tier.addEventListener('keydown', (event) => {
          if (event.target.closest('select')) return;
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            this.select(tier);
          }
        });
        tier.querySelectorAll('[data-item-select]').forEach((select) =>
          select.addEventListener('change', () => {
            this.select(tier);
            this.update();
          })
        );
      });

      this.select(this.tiers.find((tier) => tier.classList.contains('is-selected')) || this.tiers[0]);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const detail = event && event.data;
          if (!detail || detail.sectionId !== this.dataset.section || !detail.variant) return;
          this.setMainVariant(detail.variant.id);
        });
      }

      bundles.set(this.formId, this);
    }

    disconnectedCallback() {
      if (this.isConnected) return;
      if (this.unsubscribe) this.unsubscribe();
      if (bundles.get(this.formId) === this) bundles.delete(this.formId);
    }

    get selectedTier() {
      return this.tiers.find((tier) => tier.classList.contains('is-selected'));
    }

    variant(id) {
      return this.variants.find((v) => v.id === Number(id));
    }

    select(tier) {
      if (!tier) return;
      this.tiers.forEach((t) => {
        const active = t === tier;
        t.classList.toggle('is-selected', active);
        t.setAttribute('aria-checked', active);
      });
      this.update();
    }

    setMainVariant(id) {
      this.mainVariantId = Number(id);
      this.querySelectorAll('[data-item-select]').forEach((select) => {
        const option = select.querySelector(`option[value="${id}"]`);
        if (option && !option.disabled) select.value = String(id);
      });
      this.update();
    }

    /* Liste des variantes (une par article) pour une offre donnée. */
    itemIds(tier) {
      const qty = Number(tier.dataset.qty) || 1;
      const selects = Array.from(tier.querySelectorAll('[data-item-select]'));
      if (selects.length === qty) return selects.map((s) => Number(s.value));
      const form = document.getElementById(this.formId);
      const current = Number(form?.querySelector('[name="id"]')?.value) || this.mainVariantId;
      return Array.from({ length: qty }, () => current);
    }

    totals(tier) {
      const pct = Number(tier.dataset.pct) || 0;
      let now = 0;
      let compare = 0;
      this.itemIds(tier).forEach((id) => {
        const v = this.variant(id) || this.variant(this.mainVariantId);
        if (!v) return;
        // Comme le panier Shopify : remise calculée par article, arrondie au centime inférieur.
        now += v.price - Math.floor((v.price * pct) / 100);
        compare += Math.max(v.compare || 0, v.price);
      });
      return { now, was: compare };
    }

    update() {
      this.tiers.forEach((tier) => {
        const { now, was } = this.totals(tier);
        const nowEl = tier.querySelector('[data-now]');
        const wasEl = tier.querySelector('[data-was]');
        if (nowEl) nowEl.textContent = formatMoney(now, this.moneyFormat);
        if (wasEl) {
          wasEl.textContent = formatMoney(was, this.moneyFormat);
          wasEl.hidden = was <= now;
        }
      });

      const tier = this.selectedTier;
      if (!tier) return;
      document.dispatchEvent(
        new CustomEvent('lx:bundle-change', {
          detail: {
            formId: this.formId,
            label: tier.dataset.label,
            total: formatMoney(this.totals(tier).now, this.moneyFormat),
          },
        })
      );
    }

    /* On ne prend la main sur l'ajout au panier que si l'offre l'exige. */
    shouldHandle() {
      const tier = this.selectedTier;
      if (!tier) return false;
      return Number(tier.dataset.qty) > 1 || Boolean(tier.dataset.gift) || Boolean(tier.querySelector('[data-item-select]'));
    }

    items() {
      const tier = this.selectedTier;
      const grouped = new Map();
      this.itemIds(tier).forEach((id) => grouped.set(id, (grouped.get(id) || 0) + 1));
      const items = Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
      if (tier.dataset.gift && items[0]) items[0].properties = { 'Cadeau offert': tier.dataset.gift };
      return items;
    }

    async addToCart(form) {
      const button = form.querySelector('[type="submit"]');
      const spinner = form.querySelector('.loading__spinner');
      const errorWrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
      if (button?.getAttribute('aria-disabled') === 'true') return;

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const drawer = cart && cart.tagName.toLowerCase() === 'cart-drawer' ? cart : null;
      const body = { items: this.items() };
      if (drawer) {
        body.sections = drawer.getSectionsToRender().map((section) => section.id).join(',');
        body.sections_url = window.location.pathname;
        drawer.setActiveElement(document.activeElement);
      }

      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('loading');
      spinner?.classList.remove('hidden');
      if (errorWrapper) errorWrapper.hidden = true;

      try {
        const addUrl = (window.routes?.cart_add_url || '/cart/add') + '.js';
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((r) => r.json());

        if (response.status) {
          if (errorWrapper && errorMessage) {
            errorMessage.textContent = response.description || response.message;
            errorWrapper.hidden = false;
          }
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lx-bundle', cartData: response });
        }

        if (drawer) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(response);
        } else {
          window.location = window.routes?.cart_url || '/cart';
        }
      } catch (error) {
        console.error(error);
      } finally {
        button?.removeAttribute('aria-disabled');
        button?.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    }
  }

  if (!customElements.get('lx-bundle')) customElements.define('lx-bundle', LxBundle);

  /* Intercepte l'envoi du formulaire produit avant Dawn (phase de capture). */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      // form.id renverrait le champ <input name="id"> : on lit l'attribut.
      const bundle = form instanceof HTMLFormElement && bundles.get(form.getAttribute('id'));
      if (!bundle || !bundle.shouldHandle()) return;
      event.preventDefault();
      event.stopPropagation();
      bundle.addToCart(form);
    },
    true
  );

  /* ---------- Compte à rebours ---------- */
  class LxCountdown extends HTMLElement {
    connectedCallback() {
      if (this.timer) return;
      const raw = (this.dataset.end || '').trim().replace(' ', 'T');
      this.end = new Date(raw.length === 16 ? raw + ':00' : raw);
      // Démo (boutique sous mot de passe ou ?lx_demo=1) : offre de lancement qui se termine à minuit.
      if (this.dataset.demoTitle && window.lxDemo) {
        window.lxDemo.then((on) => {
          if (!on) return;
          this.end = new Date();
          this.end.setHours(24, 0, 0, 0);
          this.classList.remove('is-expired');
          const title = this.querySelector('.lx-countdown__title');
          const sub = this.querySelector('.lx-countdown__sub');
          if (title) title.innerHTML = this.dataset.demoTitle;
          if (sub) sub.textContent = this.dataset.demoSub || '';
          if (this.tick) this.tick();
        });
      }
      if (Number.isNaN(this.end.getTime())) {
        this.classList.add('is-expired');
        return;
      }
      this.parts = {
        d: this.querySelector('[data-d]'),
        h: this.querySelector('[data-h]'),
        m: this.querySelector('[data-m]'),
        s: this.querySelector('[data-s]'),
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.timer = null;
    }

    tick() {
      const left = Math.max(0, this.end.getTime() - Date.now());
      if (left === 0) {
        clearInterval(this.timer);
        this.classList.add('is-expired');
        if (this.dataset.hideExpired === 'true') this.classList.add('is-hidden');
        return;
      }
      const pad = (n) => String(n).padStart(2, '0');
      const total = Math.floor(left / 1000);
      const days = Math.floor(total / 86400);
      this.classList.toggle('has-days', days > 0);
      this.parts.d.textContent = pad(days);
      this.parts.h.textContent = pad(Math.floor((total % 86400) / 3600));
      this.parts.m.textContent = pad(Math.floor((total % 3600) / 60));
      this.parts.s.textContent = pad(total % 60);
    }
  }

  if (!customElements.get('lx-countdown')) customElements.define('lx-countdown', LxCountdown);

  /* ---------- Frise de livraison ---------- */
  class LxDelivery extends HTMLElement {
    connectedCallback() {
      const skipWeekends = this.dataset.skipWeekends === 'true';
      const lang = document.documentElement.lang || 'fr';
      const fmt = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short' });
      const addDays = (count) => {
        const date = new Date();
        let added = 0;
        while (added < count) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (!skipWeekends || (day !== 0 && day !== 6)) added += 1;
        }
        return date;
      };
      this.querySelectorAll('[data-min]').forEach((step) => {
        const min = Number(step.dataset.min) || 0;
        const max = Math.max(min, Number(step.dataset.max) || 0);
        const target = step.querySelector('[data-date]');
        if (!target) return;
        const first = fmt.format(addDays(min));
        target.textContent = min === max ? first : `${first} – ${fmt.format(addDays(max))}`;
      });
    }
  }

  if (!customElements.get('lx-delivery')) customElements.define('lx-delivery', LxDelivery);

  /* ---------- Barre d'achat collante ---------- */
  class LxStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // Déplacée sous <body> pour rester fixe même si un parent est animé.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
      this.hidden = false;

      this.form = document.getElementById(this.dataset.formId);
      const mainButton = this.form?.querySelector('[type="submit"]');
      this.querySelector('[data-sticky-btn]')?.addEventListener('click', () => {
        if (!this.form) return;
        if (this.form.requestSubmit) this.form.requestSubmit(mainButton || undefined);
        else mainButton?.click();
      });

      document.addEventListener('lx:bundle-change', (event) => {
        if (event.detail.formId !== this.dataset.formId) return;
        const label = this.querySelector('[data-sticky-label]');
        const price = this.querySelector('[data-sticky-price]');
        if (label) label.textContent = event.detail.label ? `${event.detail.label} ·` : '';
        if (price) price.textContent = event.detail.total;
      });
      // L'offre a pu être initialisée avant cette barre : on redemande son état.
      bundles.get(this.dataset.formId)?.update();

      if (!mainButton || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPast);
      }).observe(mainButton);
    }
  }

  if (!customElements.get('lx-sticky-atc')) customElements.define('lx-sticky-atc', LxStickyAtc);

  // Panier : changer la longueur d'une ligne = ajouter la nouvelle variante puis retirer l'ancienne ligne.
  document.addEventListener('change', async (event) => {
    const select = event.target.closest('.lx-cart-variant__select');
    if (!select) return;
    const cartItems = select.closest('cart-drawer-items, cart-items');
    select.disabled = true;
    try {
      await fetch(`${routes.cart_add_url}.js`, {
        ...fetchConfig('javascript'),
        body: JSON.stringify({ items: [{ id: Number(select.value), quantity: Number(select.dataset.quantity) }] }),
      });
      const cart = await (await fetch(`${routes.cart_url}.js`)).json();
      const line = cart.items.findIndex((item) => item.key === select.dataset.lineKey) + 1;
      if (cartItems && line > 0) cartItems.updateQuantity(line, 0, { currentTarget: null }, null, null);
      else window.location.reload();
    } catch (e) {
      window.location.reload();
    }
  });

  window.LX = { formatMoney };
})();
